import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AccountSelector } from './AccountSelector';
import { UserMenu } from './UserMenu';
import { useInbox } from '../../lib/hooks/useInbox';

/**
 * Sticky frosted-glass top bar. Apple-style backdrop blur, subtle bottom
 * shadow line, refined nav links with an active-state indicator that
 * tracks the current route.
 *
 * Used inside protected pages. The `title` prop is kept for compat but
 * is no longer rendered prominently (the new convention is for each
 * page to render its own <PageHeader> inside <main>).
 */
export const Header: React.FC<{ title?: string }> = ({ title }) => {
  // UX-03 — the Today nav item shows a red count badge when the inbox
  // (pending work + attention + budget alerts, merged) is non-empty.
  const { counts: inboxCounts, isLoading: inboxLoading } = useInbox(false);
  const todayBadge = !inboxLoading && inboxCounts.all > 0 ? inboxCounts.all : undefined;
  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md shadow-header border-b border-transparent dark:border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 group">
          <span
            aria-hidden
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg
                       bg-gradient-to-br from-primary-400 to-primary-600 text-white
                       text-xs font-bold shadow-card"
          >
            ⚒
          </span>
          <span className="text-sm font-semibold text-slate-900 tracking-tight">
            Forge
          </span>
          {title && (
            <span className="hidden sm:inline text-xs text-slate-500 ml-1">
              / {title}
            </span>
          )}
        </Link>

        <nav className="flex items-center gap-1 text-sm">
          <NavItem to="/" exact>Epics</NavItem>
          <NavItem to="/today" badge={todayBadge}>Today</NavItem>
          {/* UX-02 — Health/Spend/Efficiency/Insights merged into the
              /insights hub; the "Reports" dropdown is gone. */}
          <NavItem to="/insights">Insights</NavItem>
          <NavDropdown
            label="Workspace"
            items={[
              { to: '/projects', label: 'Projects' },
              { to: '/repos', label: 'Repos' },
              // Flags stays unconditional here: `me` (CurrentUser) carries no
              // admin/role field today, so there's nothing to gate on yet —
              // see UX-10 spec §6 ("if that role is available on `me`").
              { to: '/admin/flags', label: 'Flags' },
            ]}
          />
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <AccountSelector />
          <UserMenu />
        </div>
      </div>
    </header>
  );
};

/**
 * UX-03 — an optional red count badge (capped at `9+`), e.g. on the Today
 * nav item. When present it replaces the link's accessible name with
 * `"<children>, N items need attention"` so screen readers get the count
 * without relying on the visual pill.
 */
const NavItem: React.FC<{ to: string; exact?: boolean; badge?: number; children: React.ReactNode }> = ({
  to,
  exact,
  badge,
  children,
}) => (
  <NavLink
    to={to}
    end={exact}
    aria-label={
      badge
        ? `${children}, ${badge} item${badge === 1 ? '' : 's'} ${badge === 1 ? 'needs' : 'need'} attention`
        : undefined
    }
    className={({ isActive }) =>
      'px-2.5 py-1.5 rounded-md transition-colors duration-150 inline-flex items-center gap-1.5 ' +
      (isActive
        ? 'bg-primary-50 text-primary-600 font-semibold dark:bg-primary-500/25 dark:text-[#EDF1F7]'
        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')
    }
  >
    {children}
    {!!badge && (
      <span
        aria-hidden
        data-testid="today-nav-badge"
        className="inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-danger-500 text-white text-[10px] font-semibold leading-none"
      >
        {badge > 9 ? '9+' : badge}
      </span>
    )}
  </NavLink>
);

/**
 * Grouped nav root — click the label to open a popover of child links.
 * The root button shows the active style when any child path matches
 * the current pathname (matched by prefix).
 */
const NavDropdown: React.FC<{
  label: string;
  items: ReadonlyArray<{ to: string; label: string }>;
}> = ({ label, items }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const active = items.some((it) => location.pathname.startsWith(it.to));

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Auto-close when navigation switches the route — the new page renders
  // afresh and the menu shouldn't linger.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="relative" ref={ref} data-testid={`nav-dropdown-${label.toLowerCase()}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          'px-2.5 py-1.5 rounded-md transition-colors duration-150 inline-flex items-center gap-1 ' +
          (active
            ? 'bg-primary-50 text-primary-600 font-semibold dark:bg-primary-500/25 dark:text-[#EDF1F7]'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')
        }
      >
        {label}
        <span aria-hidden className="text-slate-400 text-xs">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label={label}
          className="absolute left-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40"
        >
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              role="menuitem"
              data-testid={`nav-dropdown-item-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                'block px-3 py-2 text-sm transition-colors ' +
                (isActive
                  ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-500/25 dark:text-[#EDF1F7]'
                  : 'text-slate-700 hover:bg-slate-50')
              }
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};
